import React, { useState, useEffect, useRef } from 'react';
import { Alert, Badge, Button, Form, Spinner } from 'react-bootstrap';

const ChatQueja = ({ quejaId, show, onHide, usuarioRol, contactoAsignadoNombre, onMensajeEnviado, onQuejaCompletada }) => {
    const [mensajes, setMensajes] = useState([]);
    const [nuevoMensaje, setNuevoMensaje] = useState('');
    const [archivo, setArchivo] = useState(null);
    const [fechaLimite, setFechaLimite] = useState('');
    const [loading, setLoading] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [actualizandoCompletado, setActualizandoCompletado] = useState(false);
    const [quejaInfo, setQuejaInfo] = useState(null);
    const chatContainerRef = useRef(null);
    const esResuelta = mensajes.some(m => m.estado === 'completado' || m.estado === 'resuelta');

    useEffect(() => {
        if (show && quejaId) {
            cargarMensajes();
            cargarQuejaInfo();
        }
    }, [show, quejaId]);

    useEffect(() => {
        scrollToBottom();
    }, [mensajes]);

    const cargarMensajes = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`https://api-sugerencias.conduto.com.ec/chat/${quejaId}/mensajes`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) throw new Error('Error al cargar mensajes');
            const result = await response.json();
            if (result.success) {
                setMensajes(result.data);
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const cargarQuejaInfo = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`https://api-sugerencias.conduto.com.ec/queja/${quejaId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    setQuejaInfo(result.data);
                }
            }
        } catch (error) {
            console.error('Error al cargar info de queja:', error);
        }
    };

    const scrollToBottom = () => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    };

    const handleEnviarMensaje = async (e) => {
        e.preventDefault();
        if (!nuevoMensaje.trim() && !archivo) {
            setError('Debe escribir un mensaje o adjuntar un archivo');
            return;
        }
        try {
            setEnviando(true);
            setError('');
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('mensaje', nuevoMensaje.trim());
            formData.append('usuarioRol', usuarioRol);
            if (fechaLimite && fechaLimite.trim() !== '') {
                formData.append('fechaLimite', fechaLimite);
            }
            if (archivo) {
                formData.append('archivo', archivo);
            }
            const response = await fetch(`https://api-sugerencias.conduto.com.ec/chat/${quejaId}/mensaje`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al enviar mensaje');
            }
            const result = await response.json();
            if (result.success) {
                setMensajes(prev => [...prev, result.data]);
                setNuevoMensaje('');
                setArchivo(null);
                setFechaLimite('');
                setSuccess('Mensaje enviado correctamente');
                setTimeout(() => setSuccess(''), 3000);
                if (onMensajeEnviado) {
                    onMensajeEnviado();
                }
            }
        } catch (error) {
            console.error('Error detallado:', error);
            setError(error.message || 'Error al enviar mensaje');
        } finally {
            setEnviando(false);
        }
    };

    const handleMarcarCompletado = async () => {
        try {
            setActualizandoCompletado(true);
            setError('');
            const token = localStorage.getItem('token');

            const response = await fetch(`https://api-sugerencias.conduto.com.ec/chat/${quejaId}/completar`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al marcar como completado');
            }

            const result = await response.json();

            if (result.success) {
                await enviarCorreoCompletado();

                const tipoTexto = quejaInfo?.tipo_queja === 'identificada' ? 'Sugerencia' : 'Denuncia';
                setSuccess(`${tipoTexto} marcada como completada y notificación enviada`);

                setMensajes(prev => [...prev, {
                    estado: 'completado',
                    usuario_nombre: 'Sistema',
                    mensaje: 'Tarea completada',
                    fecha_creacion: new Date().toISOString()
                }]);

                if (onQuejaCompletada) {
                    onQuejaCompletada(quejaId);
                }

                setTimeout(() => {
                    cargarMensajes();
                    cargarQuejaInfo();
                }, 1000);

                setTimeout(() => {
                    onHide();
                    if (onMensajeEnviado) {
                        onMensajeEnviado();
                    }
                }, 2000);
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setActualizandoCompletado(false);
        }
    };

    const enviarCorreoCompletado = async () => {
        try {
            if (!quejaInfo || !quejaInfo.contacto_correo) {
                console.warn('No hay correo de contacto asignado');
                return;
            }

            const token = localStorage.getItem('token');
            const usuario = JSON.parse(localStorage.getItem('usuario'));
            const adminNombre = usuario?.nombre || 'Administrador';

            const fechaActual = new Date();
            const fechaFormateada = fechaActual.toLocaleString('es-ES', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });

            const tipoTexto = quejaInfo.tipo_queja === 'identificada' ? 'Sugerencia' : 'Denuncia';
            const asunto = `Tarea Completada - ${tipoTexto}`;

            const contenido = `
<!DOCTYPE html>
<html lang="es">
<head>
    <meta charset="UTF-8">
    <title>Tarea Completada</title>
</head>
<body style="margin:0; padding:0; font-family: Arial, sans-serif; background:#ffffff;">

<table width="100%" cellpadding="0" cellspacing="0" style="background:#28a745; color:white;">
    <tr>
        <td style="padding:20px; text-align:center;">
            <h2 style="margin:0; font-size:22px; font-weight:bold;">
                Sistema de Sugerencias y Denuncias - Conduto Ecuador S.A.
            </h2>
        </td>
    </tr>
</table>

<table width="100%" cellpadding="0" cellspacing="0" style="padding:25px; color:#333;">
    <tr>
        <td>

            <h3 style="margin-top:0; color:#28a745; font-size:20px;">
                Tarea Completada
            </h3>

            <p>Hola <strong>${quejaInfo.contacto_asignado || 'Estimado/a'}</strong>,</p>
            ${quejaInfo.tipo_queja === 'identificada' ? 'sugerencia' : 'denuncia'}
            <p>El administrador ha marcado como <strong>COMPLETADA</strong> la tarea que te fue asignada.</p>

            <table width="100%" cellpadding="0" cellspacing="0" style="margin-top:20px; border-left:4px solid #28a745; border-bottom:1px solid #dddddd;">
                <tr>
                    <td style="padding:12px 15px;">
                        <p style="margin:6px 0;">
                            <strong style="color:#28a745;">Detalles de la tarea:</strong>
                        </p>
                        
                        <table style="width:100%; margin-top:10px;">
                            <tr>
                                <td style="padding:8px 0;">
                                    <strong>Completada por:</strong> ${adminNombre} (Administrador)
                                </td>
                            </tr>
                            <tr>
                                <td style="padding:8px 0;">
                                    <strong>Fecha de completación:</strong> ${fechaFormateada}
                                </td>
                            </tr>
                            ${quejaInfo.nombre ? `
                            <tr>
                                <td style="padding:8px 0;">
                                    <strong>Empleado:</strong> ${quejaInfo.nombre}
                                </td>
                            </tr>
                            ` : ''}
                            ${quejaInfo.proyecto ? `
                            <tr>
                                <td style="padding:8px 0;">
                                    <strong>Proyecto:</strong> ${quejaInfo.proyecto}
                                </td>
                            </tr>
                            ` : ''}
                        </table>
                    </td>
                </tr>
            </table>

            <table width="100%" cellpadding="0" cellspacing="0" style="margin-top:25px; background:#e8f5e9; border-left:4px solid #28a745; border-radius:5px;">
                <tr>
                    <td style="padding:15px; text-align:center;">
                        <p style="margin:10px 0; color:#2e7d32;">
                            La ${quejaInfo.tipo_queja === 'identificada' ? 'sugerencia' : 'denuncia'} ha sido procesada exitosamente y marcada como completada.
                            Gracias por tu contribución al sistema de ${quejaInfo.tipo_queja === 'identificada' ? 'sugerencias' : 'denuncias'}.
                        </p>
                    </td>
                </tr>
            </table>

            <table width="100%" cellpadding="0" cellspacing="0" style="margin-top:30px; padding-top:15px; border-top:1px solid #dee2e6; text-align:center;">
                <tr>
                    <td style="color:#6c757d; font-size:12px;">
                        <p style="margin:8px 0;">
                            Este es un correo automático del Sistema de Sugerencias y Denuncias.<br>
                            No respondas a este mensaje.
                        </p>
                        <p style="margin:8px 0;">
                            © ${new Date().getFullYear()} Conduto Ecuador S.A.
                        </p>
                    </td>
                </tr>
            </table>

        </td>
    </tr>
</table>

</body>
</html>
            `;

            // Enviar correo usando la API
            const emailResponse = await fetch(`https://api-sugerencias.conduto.com.ec/chat/${quejaId}/enviar-correo-completado`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    destinatario: quejaInfo.contacto_correo,
                    asunto: asunto,
                    contenido: contenido,
                    contacto_nombre: quejaInfo.contacto_asignado
                })
            });

            if (!emailResponse.ok) {
                throw new Error('Error al enviar correo de notificación');
            }

            const emailResult = await emailResponse.json();
            if (!emailResult.success) {
                throw new Error(emailResult.message || 'Error al enviar correo');
            }

            console.log(`Correo de completación enviado a ${quejaInfo.contacto_correo}`);

        } catch (emailError) {
            console.error('Error al enviar correo de completación:', emailError);
        }
    };

    const descargarArchivo = async (filename) => {
        try {
            const response = await fetch(`https://api-sugerencias.conduto.com.ec/chat/descargar-archivo/${filename}`);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Error ${response.status}: No se puede descargar el archivo`);
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error al descargar archivo:', error);
            setError(error.message || 'Error al descargar archivo');
        }
    };

    const formatearFecha = (fecha) => {
        const d = new Date(fecha);
        return d.toLocaleString('es-ES', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const obtenerIconoArchivo = (filename) => {
        if (!filename) return '📄';
        const extension = filename.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf': return '📕';
            case 'doc': case 'docx': return '📘';
            case 'jpg': case 'jpeg': case 'png': case 'gif': return '🖼️';
            case 'txt': return '📝';
            case 'xls': case 'xlsx': return '📊';
            case 'zip': case 'rar': return '📦';
            default: return '📄';
        }
    };

    if (!show) return null;

    return (
        <div className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>
                    <i className="bi bi-chat-left-text me-2"></i>
                    Seguimiento - {quejaInfo?.tipo_queja === 'identificada' ? 'Sugerencia' : 'Denuncia'} #{quejaId}
                    {contactoAsignadoNombre && (
                        <Badge bg="info" className="ms-2">
                            Asignado a: {contactoAsignadoNombre}
                        </Badge>
                    )}
                </h5>
                <Button variant="outline-secondary" size="sm" onClick={onHide}>
                    <i className="bi bi-arrow-left me-1"></i>
                    Volver
                </Button>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            <div
                ref={chatContainerRef}
                className="chat-container"
                style={{
                    height: '300px',
                    overflowY: 'auto',
                    padding: '10px',
                    border: '1px solid #dee2e6',
                    borderRadius: '5px',
                    marginBottom: '15px',
                    backgroundColor: '#f8f9fa'
                }}
            >
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                    </div>
                ) : mensajes.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                        <i className="bi bi-chat-left display-4"></i>
                        <p className="mt-2">No hay mensajes en el chat aún</p>
                    </div>
                ) : (
                    mensajes.map((msg, index) => (
                        <div key={index} className={`mb-3 ${msg.usuario_rol === 'admin' ? 'text-end' : ''}`}>
                            <div
                                className={`d-inline-block p-3 rounded ${msg.usuario_rol === 'admin'
                                    ? 'bg-primary text-white'
                                    : 'bg-white text-dark border'
                                    }`}
                                style={{ maxWidth: '80%' }}
                            >
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div>
                                        <strong>{msg.usuario_nombre}</strong>
                                    </div>
                                    <small>
                                        {formatearFecha(msg.fecha_creacion)}
                                    </small>
                                </div>
                                <p className="mb-2">{msg.mensaje}</p>
                                {msg.archivo_filename && (
                                    <div className="mt-2">
                                        <Button
                                            variant={msg.usuario_rol === 'admin' ? 'outline-light' : 'outline-secondary'}
                                            size="sm"
                                            onClick={() => descargarArchivo(msg.archivo_filename)}
                                        >
                                            <i className="bi bi-download me-1"></i>
                                            {obtenerIconoArchivo(msg.archivo_filename)} {msg.archivo_filename}
                                        </Button>
                                    </div>
                                )}
                                {msg.fecha_limite && (
                                    <div className="mt-2">
                                        <Badge bg="warning" text="dark">
                                            <i className="bi bi-clock me-1"></i>
                                            Nueva fecha límite: {new Date(msg.fecha_limite).toLocaleDateString('es-ES')}
                                        </Badge>
                                    </div>
                                )}
                                {msg.estado === 'completado' && (
                                    <div className="mt-2">
                                        <Badge bg="success">
                                            <i className="bi bi-check-circle me-1"></i>
                                            Completado
                                        </Badge>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {!esResuelta && (
                <Form onSubmit={handleEnviarMensaje}>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            <i className="bi bi-chat-left-text me-2"></i>
                            Responder
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={nuevoMensaje}
                            onChange={(e) => setNuevoMensaje(e.target.value)}
                            placeholder={`Escribe tu mensaje para ${contactoAsignadoNombre}...`}
                        />
                        <Form.Text className="text-muted">
                            Tu mensaje será enviado por correo
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>
                            <i className="bi bi-paperclip me-2"></i>
                            Archivo adjunto (opcional, máximo 5MB)
                        </Form.Label>
                        <Form.Control
                            type="file"
                            onChange={(e) => setArchivo(e.target.files[0])}
                            accept="*"
                        />
                        <Form.Text className="text-muted">
                            Puedes adjuntar cualquier tipo de archivo
                        </Form.Text>
                        {archivo && (
                            <Alert variant="light" className="mt-2">
                                <i className="bi bi-file-earmark me-2"></i>
                                Archivo seleccionado: {archivo.name}
                                <br />
                                <small>Tamaño: {(archivo.size / 1024 / 1024).toFixed(2)} MB</small>
                            </Alert>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>
                            <i className="bi bi-clock me-2"></i>
                            Establecer nueva fecha límite (opcional)
                        </Form.Label>
                        <Form.Control
                            type="datetime-local"
                            value={fechaLimite}
                            onChange={(e) => setFechaLimite(e.target.value)}
                            min={new Date().toISOString().slice(0, 16)}
                        />
                    </Form.Group>

                    <div className="d-flex justify-content-between">
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={enviando || (!nuevoMensaje.trim() && !archivo)}
                        >
                            {enviando ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Enviando...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-send me-1"></i>
                                    Enviar mensaje
                                </>
                            )}
                        </Button>

                        <Button
                            variant="success"
                            onClick={handleMarcarCompletado}
                            disabled={actualizandoCompletado}
                        >
                            {actualizandoCompletado ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Procesando...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check-circle me-1"></i>
                                    Marcar como completada
                                </>
                            )}
                        </Button>
                    </div>
                </Form>
            )}
            {esResuelta && (
                <div className="alert alert-success mt-3">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    <strong>{quejaInfo?.tipo_queja === 'identificada' ? 'Sugerencia' : 'Denuncia'} marcada como completada</strong>
                    <p className="mb-0 mt-1">
                        Se ha enviado una notificación por correo.
                    </p>
                </div>
            )}
        </div>
    );
};

export default ChatQueja;